import React from 'react';

import styled from 'styled-components';

import { useAppSelector } from 'store/hook';
import { ActionPlanListItemType } from 'types/actionPlans';
import { SpeedToLeadMetrics } from 'types/recommendations';

import { SpeedToLeadRankingView } from './constants';
import SpeedToLeadActionPlans from './SpeedToLeadActionPlans';
import SpeedToLeadFirstResponse from './SpeedToLeadFirstResponse';
import SpeedToLeadFollowUp from './SpeedToLeadFollowUp';
import { OverallInsight } from './speedToLeadOverallUtils';
import SpeedToLeadQualityVsSpeed, { SpeedToLeadScoreRow } from './SpeedToLeadQualityVsSpeed';
import SpeedToLeadRanking from './SpeedToLeadRanking';
import { EmptySection } from './SpeedToLeadSection.styles';

const Footer = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
`;

const COMMUNITY_COLOR_VARIABLES = [
  '--competitor-purple',
  '--inactive-orange',
  '--positive-green',
  '--flamingo-pink',
  '--turquoise',
  '--community-marker-blue',
];
const PRIMARY_COMMUNITY_COLOR_VARIABLE = '--text-primary';

const getChartColor = (variable: string) =>
  window.getComputedStyle(document.documentElement).getPropertyValue(variable).trim();

const getCommunityChartColor = (index: number) =>
  getChartColor(COMMUNITY_COLOR_VARIABLES[index % COMMUNITY_COLOR_VARIABLES.length]);

type Props = {
  actionPlans: ActionPlanListItemType[];
  actionPlansEnabled: boolean;
  actionPlansError: boolean;
  categoryRecommendationIds: Partial<Record<SpeedToLeadRankingView, number>>;
  companyId: number;
  data: SpeedToLeadMetrics;
  followUpView: string;
  insight: OverallInsight | null;
  onActionPlanOpen: (planId: number) => void;
  onCategoryDetailsOpen: (recommendationId: number) => void;
  onFollowUpViewChange: (view: string) => void;
  primaryCommunityId: number | null;
  primaryCommunityName: string | null;
};

const SpeedToLeadMetricsContent = ({
  actionPlans,
  actionPlansEnabled,
  actionPlansError,
  categoryRecommendationIds,
  companyId,
  data,
  followUpView,
  insight,
  onActionPlanOpen,
  onCategoryDetailsOpen,
  onFollowUpViewChange,
  primaryCommunityId,
  primaryCommunityName,
}: Props) => {
  const { communityMap } = useAppSelector((state) => state.global);
  const scoreRows: SpeedToLeadScoreRow[] = data.quality_vs_speed.rows
    .filter((row) => row.quality_score != null && row.speed_score != null)
    .map((row, index) => ({
      ...row,
      community_color:
        row.community_id === primaryCommunityId
          ? getChartColor(PRIMARY_COMMUNITY_COLOR_VARIABLE)
          : (communityMap[row.community_id]?.color ?? getCommunityChartColor(index)),
    }));

  if (!data.available) {
    return <EmptySection>Speed to Lead metrics are unavailable: {data.reason ?? 'unknown reason'}.</EmptySection>;
  }

  return (
    <>
      {insight && (
        <SpeedToLeadRanking
          categoryRecommendationIds={categoryRecommendationIds}
          competitorsCount={primaryCommunityId ? (communityMap[primaryCommunityId]?.competitors?.length ?? 0) : 0}
          insight={insight}
          onCategoryDetailsOpen={onCategoryDetailsOpen}
          primaryCommunityId={primaryCommunityId}
          primaryCommunityName={primaryCommunityName}
          scoreRows={scoreRows}
        />
      )}
      <SpeedToLeadQualityVsSpeed
        primaryCommunityId={primaryCommunityId}
        scoreRows={scoreRows}
        scoresAvailable={data.quality_vs_speed.available}
      />
      <SpeedToLeadFirstResponse primaryCommunityId={primaryCommunityId} rows={data.first_response.rows} />
      <SpeedToLeadFollowUp
        analysisRunId={data.analysis_run_id}
        companyId={companyId}
        communities={data.follow_up.communities}
        followUpView={followUpView}
        onFollowUpViewChange={onFollowUpViewChange}
        primaryCommunityId={primaryCommunityId}
      />
      <SpeedToLeadActionPlans
        actionPlans={actionPlans}
        actionPlansEnabled={actionPlansEnabled}
        actionPlansError={actionPlansError}
        actionPlanStatus={data.action_plan_status}
        onActionPlanOpen={onActionPlanOpen}
      />
      <Footer>This summary was generated by AI. Validate its conclusions before making operational decisions.</Footer>
    </>
  );
};

export default SpeedToLeadMetricsContent;
